/**
 * Discovery Manager - Central management for discovery progress and requirements
 * This module handles the discovery flow, ICP requirements, and disco_details storage
 */

import { 
  getDiscoveryProgress,
  updateDiscoveryProgress,
  addToDiscoveryQueue,
  markDiscoveryItemCompleted
} from './api';

// Topic to section mapping for discovery
const TOPIC_TO_SECTION_MAPPING = {
  // Role & Title
  'role': 'role_title',
  'title': 'role_title',
  
  // Experience
  'experience': 'experience_level',
  'years_of_experience': 'experience_level',
  
  // Skills
  'skills': 'technical_skills',
  'technical_skills': 'technical_skills',
  'soft_skills': 'soft_skills',
  
  // Location
  'location': 'location_preferences',
  'remote': 'location_preferences',
  
  // Company
  'company': 'company_preferences',
  'company_stage': 'company_stage',
  'company_size': 'company_size',
  'industry': 'industry',
  
  // Compensation
  'compensation': 'compensation_range',
  'salary': 'compensation_range',
  
  // Culture
  'culture': 'culture_fit',
  'values': 'culture_fit',
  
  // Business Model
  'business_model': 'business_model',
  
  // Other preferences
  'benefits': 'benefits_preferences',
  'growth': 'growth_opportunities',
  'work_life_balance': 'work_life_balance'
};

// Discovery status states
const DISCOVERY_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  DISCOVERY_DONE: 'discovery_done',
  DISCOVERY_COMPLETE: 'discovery_complete'
};

/**
 * Initialize discovery progress from ICP requirements
 * @param {Array} icpRequirements - Initial ICP requirements from runAIMatches
 * @returns {Object} Initial discovery progress structure
 */
export function initializeDiscoveryProgress(icpRequirements = []) {
  const discoveryProgress = {
    icp_requirements: icpRequirements || [],
    discovery_status: {},
    completed_topics: [],
    current_topic: null,
    last_updated: new Date().toISOString()
  };
  
  // Initialize discovery status for each requirement
  icpRequirements.forEach(req => {
    if (req.section_id) {
      discoveryProgress.discovery_status[req.section_id] = {
        status: DISCOVERY_STATUS.NOT_STARTED,
        bool_str: req.bool_str || '',
        section_name: req.section_name || '',
        section_type: req.section_type || 'Must Have',
        section_order: req.section_order || 0,
        completed_at: null,
        component_data: null
      };
    }
  });
  
  return discoveryProgress;
}

/**
 * Get the section ID for a given topic
 * @param {string} topic - The discovery topic
 * @returns {string|null} The corresponding section ID
 */
export function getSectionIdForTopic(topic) {
  return TOPIC_TO_SECTION_MAPPING[topic] || null;
}

/**
 * Check if a topic's discovery is complete
 * @param {Object} discoveryProgress - Current discovery progress
 * @param {string} topic - The topic to check
 * @returns {boolean} Whether the topic's discovery is complete
 */
export function isTopicDiscoveryComplete(discoveryProgress, topic) {
  const sectionId = getSectionIdForTopic(topic);
  if (!sectionId || !discoveryProgress.discovery_status[sectionId]) {
    return false;
  }
  
  const status = discoveryProgress.discovery_status[sectionId].status;
  return status === DISCOVERY_STATUS.DISCOVERY_DONE || 
         status === DISCOVERY_STATUS.DISCOVERY_COMPLETE;
}

/**
 * Update discovery progress for a topic
 * @param {Object} discoveryProgress - Current discovery progress
 * @param {string} topic - The topic being updated
 * @param {Object} updates - Updates to apply
 * @returns {Object} Updated discovery progress
 */
export function updateTopicDiscovery(discoveryProgress, topic, updates) {
  const sectionId = getSectionIdForTopic(topic);
  if (!sectionId) {
    console.warn(`No section mapping found for topic: ${topic}`);
    return discoveryProgress;
  }
  
  const updatedProgress = { ...discoveryProgress };
  
  // Initialize section if it doesn't exist
  if (!updatedProgress.discovery_status[sectionId]) {
    updatedProgress.discovery_status[sectionId] = {
      status: DISCOVERY_STATUS.NOT_STARTED,
      bool_str: '',
      section_name: '',
      section_type: 'Must Have',
      section_order: 0,
      completed_at: null,
      component_data: null
    };
  }
  
  // Apply updates
  updatedProgress.discovery_status[sectionId] = {
    ...updatedProgress.discovery_status[sectionId],
    ...updates,
    last_updated: new Date().toISOString()
  };
  
  // Update ICP requirements if bool_str changed
  if (updates.bool_str !== undefined) {
    const reqIndex = updatedProgress.icp_requirements.findIndex(
      req => req.section_id === sectionId
    );
    
    if (reqIndex >= 0) {
      updatedProgress.icp_requirements[reqIndex] = {
        ...updatedProgress.icp_requirements[reqIndex],
        bool_str: updates.bool_str
      };
    } else {
      // Add new requirement if it doesn't exist
      updatedProgress.icp_requirements.push({
        section_id: sectionId,
        bool_str: updates.bool_str,
        section_name: updatedProgress.discovery_status[sectionId].section_name,
        section_type: updatedProgress.discovery_status[sectionId].section_type,
        section_order: updatedProgress.discovery_status[sectionId].section_order
      });
    }
  }
  
  // Mark topic as completed if status is discovery_done
  if (updates.status === DISCOVERY_STATUS.DISCOVERY_DONE) {
    updatedProgress.discovery_status[sectionId].completed_at = new Date().toISOString();
    if (!updatedProgress.completed_topics.includes(topic)) {
      updatedProgress.completed_topics.push(topic);
    }
  }
  
  // Update current topic
  if (updates.status === DISCOVERY_STATUS.IN_PROGRESS) {
    updatedProgress.current_topic = topic;
  }
  
  updatedProgress.last_updated = new Date().toISOString();
  
  return updatedProgress;
}

/**
 * Get the current ICP requirements from discovery progress
 * @param {Object} discoveryProgress - Current discovery progress
 * @returns {Array} Current ICP requirements
 */
export function getCurrentICPRequirements(discoveryProgress) {
  if (!discoveryProgress || !discoveryProgress.icp_requirements) {
    return [];
  }
  
  // Filter out empty bool_str requirements
  return discoveryProgress.icp_requirements.filter(req => req.bool_str && req.bool_str.trim());
}

/**
 * Save discovery progress to database using the robust discovery-progress API
 * @param {string} sessionId - The session ID
 * @param {Object} discoveryProgress - Discovery progress to save
 * @returns {Promise<Object>} Updated discovery progress
 */
export async function saveDiscoveryProgress(sessionId, discoveryProgress) {
  try {
    // Convert discovery progress to queue items format for the discovery-progress API
    const queueItems = [];
    
    if (discoveryProgress.icp_requirements) {
      discoveryProgress.icp_requirements.forEach(req => {
        const status = discoveryProgress.discovery_status?.[req.section_id];
        queueItems.push({
          section_id: req.section_id,
          section_name: req.section_name || status?.section_name || '',
          bool_str: req.bool_str || '',
          section_type: req.section_type || 'Must Have',
          disco_done: status?.status === 'discovery_done' || status?.status === 'discovery_complete',
          section_order: req.section_order || 0,
          topic: inferTopicFromSectionId(req.section_id)
        });
      });
    }
    
    // Use the robust discovery-progress API (auto-creates sessions)
    const result = await updateDiscoveryProgress(sessionId, queueItems);
    console.log('✅ Discovery progress saved via discovery-progress API:', result);
    return result;
  } catch (error) {
    console.error('❌ Error saving discovery progress via discovery-progress API:', error);
    throw error;
  }
}

/**
 * Infer topic from section_id (helper function)
 * @param {string} sectionId - The section ID
 * @returns {string} The inferred topic
 */
function inferTopicFromSectionId(sectionId) {
  // Simple topic inference based on section ID patterns
  if (sectionId.includes('skill')) return 'skills';
  if (sectionId.includes('experience')) return 'experience';
  if (sectionId.includes('location')) return 'location';
  if (sectionId.includes('role') || sectionId.includes('title')) return 'title';
  if (sectionId.includes('company')) return 'company';
  if (sectionId.includes('stage')) return 'company_stage';
  if (sectionId.includes('education')) return 'education';
  
  // Default fallback
  return 'skills';
}

/**
 * Load discovery progress from database using the robust discovery progress API
 * @param {string} sessionId - The session ID
 * @returns {Promise<Object>} Discovery progress
 */
export async function loadDiscoveryProgress(sessionId) {
  try {
    // Use the robust discovery progress API exclusively
    const queueResult = await getDiscoveryProgress(sessionId);
    if (queueResult && queueResult.items && queueResult.items.length > 0) {
      console.log('✅ Discovery progress loaded from discovery progress API:', queueResult.items);
      
      // Convert queue items to discovery progress format
      const discoveryProgress = {
        icp_requirements: queueResult.items.map(item => ({
          section_id: item.section_id,
          section_name: item.section_name,
          bool_str: item.bool_str,
          section_type: item.section_type,
          section_order: item.section_order || 0
        })),
        discovery_status: {},
        completed_topics: [],
        current_topic: null,
        last_updated: new Date().toISOString()
      };
      
      // Build discovery status from queue items
      queueResult.items.forEach(item => {
        discoveryProgress.discovery_status[item.section_id] = {
          status: item.disco_done ? DISCOVERY_STATUS.DISCOVERY_DONE : DISCOVERY_STATUS.NOT_STARTED,
          bool_str: item.bool_str,
          section_name: item.section_name,
          section_type: item.section_type,
          section_order: item.section_order || 0,
          completed_at: item.disco_done ? new Date().toISOString() : null,
          component_data: null
        };
        
        // Add to completed topics if done
        if (item.disco_done && item.topic && !discoveryProgress.completed_topics.includes(item.topic)) {
          discoveryProgress.completed_topics.push(item.topic);
        }
      });
      
      return discoveryProgress;
    }
    
    console.log('No discovery progress found for session (expected for new sessions)');
    return null;
  } catch (error) {
    console.error('❌ Error loading discovery progress:', error);
    return null;
  }
}

/**
 * Get discovery completion percentage
 * @param {Object} discoveryProgress - Current discovery progress
 * @returns {number} Completion percentage (0-100)
 */
export function getDiscoveryCompletionPercentage(discoveryProgress) {
  if (!discoveryProgress || !discoveryProgress.discovery_status) {
    return 0;
  }
  
  const sections = Object.values(discoveryProgress.discovery_status);
  if (sections.length === 0) return 0;
  
  const completedSections = sections.filter(section => 
    section.status === DISCOVERY_STATUS.DISCOVERY_DONE || 
    section.status === DISCOVERY_STATUS.DISCOVERY_COMPLETE
  ).length;
  
  return Math.round((completedSections / sections.length) * 100);
}

/**
 * Get next topic to discover based on role type and discovery order
 * @param {Object} discoveryProgress - Current discovery progress
 * @param {Array} discoveryOrder - Ordered list of topics for the role type
 * @returns {string|null} Next topic to discover
 */
export function getNextDiscoveryTopic(discoveryProgress, discoveryOrder) {
  if (!discoveryOrder || discoveryOrder.length === 0) {
    return null;
  }
  
  // Find the first topic in the order that hasn't been completed
  for (const topic of discoveryOrder) {
    if (!isTopicDiscoveryComplete(discoveryProgress, topic)) {
      return topic;
    }
  }
  
  return null; // All topics completed
}

/**
 * Create a discovery checkpoint
 * @param {Object} discoveryProgress - Current discovery progress
 * @param {string} checkpointName - Name for the checkpoint
 * @returns {Object} Checkpoint data
 */
export function createDiscoveryCheckpoint(discoveryProgress, checkpointName) {
  return {
    name: checkpointName,
    timestamp: new Date().toISOString(),
    discovery_progress: JSON.parse(JSON.stringify(discoveryProgress)), // Deep clone
    completion_percentage: getDiscoveryCompletionPercentage(discoveryProgress)
  };
}

/**
 * Restore from a discovery checkpoint
 * @param {Object} checkpoint - Checkpoint to restore from
 * @returns {Object} Restored discovery progress
 */
export function restoreFromCheckpoint(checkpoint) {
  if (!checkpoint || !checkpoint.discovery_progress) {
    throw new Error('Invalid checkpoint data');
  }
  
  return {
    ...checkpoint.discovery_progress,
    restored_from: checkpoint.name,
    restored_at: new Date().toISOString()
  };
}

/**
 * Merge discovery progress updates
 * @param {Object} currentProgress - Current discovery progress
 * @param {Object} updates - Updates to merge
 * @returns {Object} Merged discovery progress
 */
export function mergeDiscoveryProgress(currentProgress, updates) {
  const merged = { ...currentProgress };
  
  // Merge ICP requirements
  if (updates.icp_requirements) {
    const existingReqMap = new Map(
      merged.icp_requirements.map(req => [req.section_id, req])
    );
    
    updates.icp_requirements.forEach(req => {
      existingReqMap.set(req.section_id, req);
    });
    
    merged.icp_requirements = Array.from(existingReqMap.values());
  }
  
  // Merge discovery status
  if (updates.discovery_status) {
    merged.discovery_status = {
      ...merged.discovery_status,
      ...updates.discovery_status
    };
  }
  
  // Merge completed topics
  if (updates.completed_topics) {
    merged.completed_topics = [
      ...new Set([...merged.completed_topics, ...updates.completed_topics])
    ];
  }
  
  // Update other fields
  if (updates.current_topic !== undefined) {
    merged.current_topic = updates.current_topic;
  }
  
  merged.last_updated = new Date().toISOString();
  
  return merged;
}

// Export discovery status constants for use in components
export { DISCOVERY_STATUS };
